import React from "react";
import { Link } from "react-router-dom";
import { FiCode, FiCompass, FiGlobe, FiInbox, FiShare2, FiZap } from "react-icons/fi";
import ContentStatusPill, { type ContentPublishStatus } from "../ContentStatusPill";
import GatedButton from "../GatedButton";
import { PreviewLinks } from "../../Pages/Content/ContentListView";

export type EditorActionBarProps = {
	/** Page-editor only: page-tree toggle + the Blocs/Markdown/JSON switcher. The content-data editor
	 *  has neither, so it passes nothing and the bar closes up around it. */
	leadingSlot?: React.ReactNode;

	/** Where this content stands: « En ligne » / « Modifié » + its live Dev / Prod links. */
	status: ContentPublishStatus;
	previewUrls?: string[];
	productionUrls?: string[];

	onShowConnections?: () => void;
	connectionsDisabled?: boolean;

	/** « Par ou arrive-t-on ici depuis l'accueil ? » : ouvre l'analyse d'acces. */
	onShowReachability?: () => void;
	reachabilityDisabled?: boolean;

	/** Analyser / Traduire only make sense inside an edit session. */
	isEditing: boolean;
	onAnalyze?: () => void;
	analyzeDisabled?: boolean;
	/** Route to the translation workspace. A Link, so middle-click / open-in-tab still work. */
	translateHref?: string;
	onCreateTicket?: () => void;
	/** Content-data editor only : the page editor edits JSON through its view switcher instead. */
	onOpenJson?: () => void;
	jsonDisabled?: boolean;
};

/**
 * Same bordered format as the top bar's commit actions, one size down, and sized in clamp() so it
 * scales with the viewport instead of stepping at breakpoints. Arbitrary values on purpose: they
 * compile to unique class names, which the second Tailwind build (the renderer stylesheet) cannot
 * clobber : a plain utility from that later sheet beats an earlier responsive variant, which is what
 * silently killed xl:grid-cols-2 on /account.
 */
const BTN = "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-[clamp(0.4rem,0.7vw,0.75rem)] py-[clamp(0.15rem,0.35vw,0.25rem)] text-[clamp(0.65rem,0.75vw,0.75rem)] font-semibold transition disabled:cursor-not-allowed disabled:opacity-50";
const NEUTRAL = `${BTN} border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50`;
/** Below this the bar keeps the icons and drops the words : one line is non-negotiable. */
const LABEL = "max-[1150px]:hidden";

/**
 * The secondary bar, directly under the app's top bar and identical on the page editor and the
 * content-data editor (the two used to disagree on what went where, and the content editor even
 * repeated its own title). The commit actions are NOT here : they live in the top bar itself, via
 * {@link EditorCommitActions}, so they never move.
 *
 * Left group aligns left, right group takes the rest and aligns right. The left group is only rendered
 * when it HAS something: the content editor has no tree toggle and no Connexions, and an empty flex-1
 * column there would silently eat half the bar and force everything else to wrap.
 */
const EditorActionBar: React.FC<EditorActionBarProps> = ({
	leadingSlot,
	status,
	previewUrls,
	productionUrls,
	onShowConnections,
	connectionsDisabled,
	onShowReachability,
	reachabilityDisabled,
	isEditing,
	onAnalyze,
	analyzeDisabled,
	translateHref,
	onCreateTicket,
	onOpenJson,
	jsonDisabled
}) => (
	<div
		data-editor-action-bar
		className="sticky top-0 z-30 w-full shrink-0 overflow-hidden border-b border-slate-200 bg-white/95 px-4 py-[clamp(0.3rem,0.6vw,0.5rem)] backdrop-blur supports-[backdrop-filter]:bg-white/80 sm:px-6"
	>
		{/* ONE line, always : nothing here wraps. What gives instead: the buttons scale (clamp), and
		    below 1150px they drop their labels and keep their icons. */}
		<div className="flex flex-nowrap items-center gap-x-3">
			{(leadingSlot || onShowConnections) && (
				<div className="flex min-w-0 flex-nowrap items-center justify-start gap-2">
					{leadingSlot}
					{onShowConnections && (
						<button
							type="button"
							onClick={onShowConnections}
							disabled={connectionsDisabled}
							title="Connexions"
							className={NEUTRAL}
						>
							<FiShare2 className="h-3.5 w-3.5 shrink-0" /> <span className={LABEL}>Connexions</span>
						</button>
					)}
				</div>
			)}

			<div className="flex min-w-0 flex-1 flex-nowrap items-center justify-end gap-2">
				{/* Where it stands, and where to go see it. */}
				<div className="flex shrink-0 flex-nowrap items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5">
					<ContentStatusPill status={status} />
					{(previewUrls || productionUrls) && (
						<PreviewLinks previewUrls={previewUrls ?? []} productionUrls={productionUrls ?? []} />
					)}
				</div>

				{/* Analyser and Traduire change the content, so they belong to an edit session : same rule
				    as Dépublier / Supprimer in the top bar. Reading stays a short, quiet bar. */}
				{isEditing && onAnalyze && (
					<button
						type="button"
						onClick={onAnalyze}
						disabled={analyzeDisabled}
						title="Analyser"
						className={`${BTN} border-purple-200 bg-purple-50 text-purple-700 hover:border-purple-300 hover:bg-purple-100`}
					>
						<FiZap className="h-3.5 w-3.5 shrink-0" /> <span className={LABEL}>Analyser</span>
					</button>
				)}
				{isEditing && translateHref && (
					<Link
						to={translateHref}
						title="Traduire"
						className={`${BTN} border-blue-200 bg-blue-50 text-blue-700 hover:border-blue-300 hover:bg-blue-100`}
					>
						<FiGlobe className="h-3.5 w-3.5 shrink-0" /> <span className={LABEL}>Traduire</span>
					</Link>
				)}
				{/* Toujours rendu quand le parent fournit l'action : sans feature:tickets:write le bouton
				    reste visible mais grisé-cliquable et le clic explique la permission manquante. */}
				{onCreateTicket && (
					<GatedButton
						token="feature:tickets:write"
						onClick={onCreateTicket}
						title="Créer un ticket lié à ce contenu"
						className={`${BTN} border-amber-200 bg-amber-50 text-amber-700 hover:border-amber-300 hover:bg-amber-100`}
					>
						<FiInbox className="h-3.5 w-3.5 shrink-0" /> <span className={LABEL}>Ticket</span>
					</GatedButton>
				)}
				{/* L'analyse d'acces se lit aussi hors edition : c'est un diagnostic, pas une modification. */}
				{onShowReachability && (
					<button
						type="button"
						onClick={onShowReachability}
						disabled={reachabilityDisabled}
						title="Par où un visiteur arrive-t-il sur ce contenu ?"
						className={`${BTN} border-teal-200 bg-teal-50 text-teal-700 hover:border-teal-300 hover:bg-teal-100`}
					>
						<FiCompass className="h-3.5 w-3.5 shrink-0" /> <span className={LABEL}>Chemin d'accès</span>
					</button>
				)}
				{onOpenJson && (
					<button type="button" onClick={onOpenJson} disabled={jsonDisabled} title="JSON" className={NEUTRAL}>
						<FiCode className="h-3.5 w-3.5 shrink-0" /> <span className={LABEL}>JSON</span>
					</button>
				)}
			</div>
		</div>
	</div>
);

export default EditorActionBar;
